import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import useIsMobile from "@/lib/useIsMobile";
import { toast } from "sonner";
import { ListChecks, Loader2, Search, CheckCircle2, ChevronDown, ChevronRight, Plus, X, AlertTriangle, CalendarClock, Trophy } from "lucide-react";

const ASSOC_COLOR = { PRCA: "#003087", CCA: "#8C6239", CPRA: "#C8000F", APRA: "#0A7D5A" };
const CURRENT_SEASON = { PRCA: 2026, CPRA: 2026, CCA: 2025, APRA: 2026 };
const money = (n) => (n == null ? "—" : "$" + Number(n).toLocaleString());

function Bar({ value, max, color }) {
  const pct = max ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-2 rounded-full bg-slate-200/70 overflow-hidden mt-1">
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

export default function RodeoCounts() {
  const m = useIsMobile();
  const [name, setName] = useState("");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({});   // `${assoc}:${event}` -> bool
  const [busy, setBusy] = useState("");    // entry key currently saving
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    api.get("/me/competitor").then((r) => {
      const n = (r.data?.competitor_name || "").trim();
      if (n) { setName(n); setInput(n); }
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!name) return;
    setLoading(true);
    api.get(`/my/rodeo-counts?name=${encodeURIComponent(name)}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [name]);
  useEffect(() => { load(); }, [load]);

  const saveDesig = async (assoc, ev, entry, patch) => {
    const key = `${entry.rodeo_id}:${ev}`;
    setBusy(key);
    try {
      await api.put("/my/count-designation", {
        rodeo_id: entry.rodeo_id, event: ev, association: assoc, name: data.name,
        rodeo_name: entry.rodeo_name, added_money: entry.added_money,
        designation: patch.designation ?? entry.designation,
        planned: patch.planned ?? entry.planned, manual: entry.manual,
      });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't save"); }
    finally { setBusy(""); }
  };
  // Official/Unofficial is a single per-rodeo value (applies to all events at that rodeo).
  const saveOfficial = async (entry, ev, official) => {
    setBusy(`${entry.rodeo_id}:${ev}`);
    try {
      await api.put("/my/rodeo-official", { rodeo_id: entry.rodeo_id, official });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't save"); }
    finally { setBusy(""); }
  };
  const removeManual = async (ev, entry) => {
    setBusy(`${entry.rodeo_id}:${ev}`);
    try { await api.delete(`/my/count-designation?rodeo_id=${encodeURIComponent(entry.rodeo_id)}&event=${encodeURIComponent(ev)}`); load(); }
    catch { toast.error("Couldn't remove"); } finally { setBusy(""); }
  };

  // gather low-value officials across all associations for the review nudge
  const review = [];
  (data?.associations || []).forEach((a) => (a.events || []).forEach((e) =>
    (e.detail || []).forEach((d) => { if (d.low_value) review.push({ assoc: a.association, event: e.event, ...d }); })));

  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const sub = m ? "text-slate-400" : "text-slate-500";

  return (
    <div className={page} data-testid="rodeo-counts-page">
      <div className={`px-4 sm:px-6 py-5 border-b ${m ? "border-white/10" : "border-[#E4E7EB]"}`}>
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <ListChecks className="w-6 h-6 text-[#C8000F]" />
            <h1 className={`font-display font-bold text-xl ${title}`}>My Rodeo Counts</h1>
          </div>
          <button onClick={() => setShowAdd((v) => !v)} data-testid="counts-add-toggle"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-[#C8000F]">
            <Plus className="w-4 h-4" /> Add planned rodeo
          </button>
        </div>
        <div className={`flex items-center gap-2 rounded-lg border px-3 max-w-md ${card}`}>
          <Search className="w-4 h-4 text-slate-400" />
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && setName(input.trim())}
            placeholder="Your name as it appears in results/draws" data-testid="counts-name-input"
            className={`flex-1 bg-transparent py-2.5 text-sm focus:outline-none ${m ? "text-slate-100 placeholder:text-slate-500" : "text-[#14181F]"}`} />
          <button onClick={() => setName(input.trim())} data-testid="counts-go" className="text-sm font-bold text-[#C8000F]">Go</button>
        </div>
      </div>

      {showAdd && <AddPlanned m={m} athlete={data?.name || name} onDone={() => { setShowAdd(false); load(); }} />}

      <div className="px-4 sm:px-6 py-4 max-w-3xl space-y-4">
        {loading && <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>}

        {!loading && review.length > 0 && (
          <div className={`rounded-xl border p-4 ${m ? "bg-[#B8860B]/10 border-[#B8860B]/30" : "bg-[#FBF3DC] border-[#E4C878]"}`} data-testid="counts-review">
            <div className="flex items-center gap-2 font-bold text-[#B8860B] mb-2 text-sm"><AlertTriangle className="w-4 h-4" /> Review {review.length} low-value official{review.length > 1 ? "s" : ""}</div>
            <p className={`text-xs mb-3 ${sub}`}>These are counting as <b>Official</b> but their added money is below your threshold. Consider switching them to Unofficial to protect your best {review.length > 1 ? "counts" : "count"}.</p>
            <div className="space-y-1.5">
              {review.map((r) => (
                <div key={`${r.rodeo_id}:${r.event}`} className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 ${m ? "bg-white/5" : "bg-white"}`} data-testid={`review-${r.rodeo_id}`}>
                  <div className="min-w-0">
                    <div className={`text-sm font-semibold truncate ${title}`}>{r.rodeo_name}</div>
                    <div className="text-xs text-slate-400">{r.assoc} · {r.event} · {money(r.added_money)} added</div>
                  </div>
                  <button onClick={() => saveOfficial(r, r.event, false)} disabled={busy === `${r.rodeo_id}:${r.event}`}
                    data-testid={`review-downgrade-${r.rodeo_id}`}
                    className="shrink-0 text-xs font-bold rounded-md px-2.5 py-1.5 bg-[#B8860B] text-white disabled:opacity-50">Make Unofficial</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && data && data.associations.length === 0 && (
          <div className={`rounded-xl border border-dashed p-8 text-center ${m ? "border-white/15 text-slate-400" : "border-[#D7DBE0] text-slate-500"}`}>
            No rodeo entries found for “{data.name}” yet. Add a planned rodeo above, enter a rodeo, or check the spelling of your name.
          </div>
        )}

        {!loading && data && data.associations.map((a) => (
          <div key={a.association} className={`rounded-xl border overflow-hidden ${card}`} data-testid={`counts-assoc-${a.association}`}>
            <div className="px-4 py-3 flex items-center justify-between" style={{ background: (ASSOC_COLOR[a.association] || "#333") + (m ? "22" : "12") }}>
              <div className="font-display font-bold flex items-center gap-2" style={{ color: ASSOC_COLOR[a.association] }}>
                {a.association} <span className="text-xs font-normal text-slate-400">{a.season} season</span>
              </div>
              {!a.verified && a.configured && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#B8860B]/20 text-[#B8860B]">unverified rules</span>}
            </div>
            {!a.configured ? (
              <div className="px-4 py-4 text-sm text-slate-400">{a.season} rules for {a.association} have not been configured yet.</div>
            ) : (
              <div className="divide-y divide-slate-100/60">
                {a.events.map((e) => {
                  const okey = `${a.association}:${e.event}`;
                  const isOpen = !!open[okey];
                  return (
                  <div key={e.event} data-testid={`counts-event-${e.event.replace(/\s/g, "-")}`}>
                    <button onClick={() => setOpen((o) => ({ ...o, [okey]: !o[okey] }))} data-testid={`counts-expand-${e.event.replace(/\s/g, "-")}`}
                      className="w-full px-4 py-3 text-left">
                      <div className="flex items-center justify-between gap-2">
                        <div className={`font-semibold text-sm flex items-center gap-1.5 ${title}`}>
                          {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                          {e.event}
                        </div>
                        {e.rule_type === "max_official" ? (
                          <div className="text-sm font-display font-bold" style={{ color: ASSOC_COLOR[a.association] }}>{e.official} / {e.max_official}</div>
                        ) : e.rule_type === "min_required" ? (
                          <div className={`text-sm font-display font-bold ${e.achieved ? "text-[#3B8A2B]" : ""}`} style={!e.achieved ? { color: ASSOC_COLOR[a.association] } : {}}>{e.completed} / {e.min_required}</div>
                        ) : <div className="text-xs text-slate-400">No count requirement</div>}
                      </div>
                      {e.rule_type === "max_official" && (
                        <div className="pl-5.5" style={{ paddingLeft: "1.375rem" }}>
                          <Bar value={e.official} max={e.max_official} color={ASSOC_COLOR[a.association]} />
                          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-500">
                            <span>Official <b className={title}>{e.official}</b></span>
                            <span>Unofficial <b className={title}>{e.unofficial}</b></span>
                            <span>Remaining <b className={title}>{e.official_remaining}</b></span>
                            {e.planned_official > 0 && <span>Planned <b className={title}>{e.planned_official}</b> → Projected <b className={title}>{e.projected_official} / {e.max_official}</b></span>}
                          </div>
                        </div>
                      )}
                      {e.rule_type === "min_required" && (
                        <div style={{ paddingLeft: "1.375rem" }}>
                          <Bar value={e.completed} max={e.min_required} color={e.achieved ? "#3B8A2B" : ASSOC_COLOR[a.association]} />
                          <div className="mt-1.5 text-xs text-slate-500">
                            {e.achieved ? <span className="text-[#3B8A2B] font-semibold flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Requirement achieved</span>
                              : <span><b className={title}>{e.remaining}</b> more required</span>}
                          </div>
                        </div>
                      )}
                    </button>

                    {isOpen && (
                      <div className={`px-4 pb-3 space-y-2 ${m ? "" : ""}`}>
                        {(e.detail || []).length === 0 && <div className="text-xs text-slate-400 pl-1">No entries.</div>}
                        {(e.detail || []).map((d) => {
                          const bkey = `${d.rodeo_id}:${e.event}`;
                          const saving = busy === bkey;
                          return (
                          <div key={d.rodeo_id} className={`rounded-lg border p-3 ${m ? "bg-white/5 border-white/10" : "bg-slate-50 border-slate-200"}`} data-testid={`entry-${d.rodeo_id}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className={`text-sm font-semibold flex items-center gap-1.5 ${title}`}>
                                  {d.planned ? <CalendarClock className="w-3.5 h-3.5 text-[#E68A2E]" /> : <Trophy className="w-3.5 h-3.5 text-[#3B8A2B]" />}
                                  <span className="truncate">{d.rodeo_name}</span>
                                </div>
                                <div className="text-xs text-slate-400 mt-0.5">
                                  {money(d.added_money)} added
                                  {e.official_unofficial && <> · recommend <b className={d.recommendation === "official" ? "text-[#3B8A2B]" : "text-slate-500"}>{d.recommendation}</b></>}
                                  {d.low_value && <span className="text-[#B8860B]"> · below threshold</span>}
                                </div>
                              </div>
                              {d.manual && (
                                <button onClick={() => removeManual(e.event, d)} disabled={saving} data-testid={`entry-remove-${d.rodeo_id}`} className="shrink-0 text-slate-400 hover:text-[#C8000F]"><X className="w-4 h-4" /></button>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 mt-2.5">
                              {e.official_unofficial && (
                                <select value={d.designation === "unofficial" ? "unofficial" : "official"}
                                  onChange={(ev2) => saveOfficial(d, e.event, ev2.target.value === "official")}
                                  disabled={saving} data-testid={`entry-desig-${d.rodeo_id}`}
                                  className={`text-xs font-bold rounded-lg border px-2.5 py-1.5 ${d.designation === "unofficial" ? "border-[#8A94A6] text-[#8A94A6]" : "border-[#3B8A2B] text-[#3B8A2B]"} ${m ? "bg-white/5" : "bg-white"}`}>
                                  <option value="official">Official</option>
                                  <option value="unofficial">Unofficial</option>
                                </select>
                              )}
                              <button onClick={() => saveDesig(a.association, e.event, d, { planned: !d.planned })} disabled={saving} data-testid={`entry-planned-${d.rodeo_id}`}
                                className={`text-xs font-bold rounded-lg px-2.5 py-1.5 border ${d.planned ? "border-[#E68A2E] text-[#E68A2E]" : (m ? "border-white/15 text-slate-300" : "border-slate-300 text-slate-600")}`}>
                                {d.planned ? "Planned" : "Confirmed"}
                              </button>
                              {saving && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        <p className="text-[11px] text-slate-400">Counts are driven by the association rule tables in Settings. Official/Unofficial recommendations use the rodeo's added money vs the configured threshold. Planned entries feed the projection but don't count until you mark them Confirmed.</p>
      </div>
    </div>
  );
}

function AddPlanned({ m, athlete, onDone }) {
  const [assoc, setAssoc] = useState("PRCA");
  const [events, setEvents] = useState([]);
  const [event, setEvent] = useState("");
  const [rodeoName, setRodeoName] = useState("");
  const [addedMoney, setAddedMoney] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const season = CURRENT_SEASON[assoc] || 2026;
    api.get(`/count-rules?association=${assoc}&season=${season}`).then((r) => {
      const evs = (r.data?.events || []).map((e) => e.event);
      setEvents(evs); setEvent(evs[0] || "");
    }).catch(() => { setEvents([]); setEvent(""); });
  }, [assoc]);

  const save = async () => {
    const ev = event || events[0];
    if (!ev) { toast.error(`No count rules set for ${assoc} ${CURRENT_SEASON[assoc] || 2026} yet — add them in Settings → Rodeo Count Rules`); return; }
    if (!rodeoName.trim()) { toast.error("Enter a rodeo name"); return; }
    setSaving(true);
    try {
      const mny = addedMoney === "" ? 0 : Number(addedMoney);
      await api.put("/my/count-designation", {
        rodeo_id: `manual:${(crypto.randomUUID && crypto.randomUUID()) || Date.now()}`,
        event: ev, association: assoc, name: athlete, manual: true, planned: true,
        rodeo_name: rodeoName.trim(), added_money: mny,
        designation: mny >= 2000 ? "official" : "unofficial",
      });
      toast.success("Planned rodeo added");
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't add"); }
    finally { setSaving(false); }
  };

  const inp = m ? "bg-white/5 border-white/15 text-slate-100" : "bg-white border-slate-300 text-[#14181F]";
  const noRules = events.length === 0;
  return (
    <div className={`px-4 sm:px-6 py-4 border-b ${m ? "border-white/10 bg-white/[0.03]" : "border-[#E4E7EB] bg-[#FBF7F2]"}`} data-testid="add-planned-form">
      <div className={`font-display font-bold mb-3 text-sm ${m ? "text-slate-100" : "text-[#14181F]"}`}>Add a rodeo you plan to enter</div>
      <div className="flex flex-wrap gap-2 max-w-3xl">
        <select value={assoc} onChange={(e) => setAssoc(e.target.value)} data-testid="add-assoc" className={`text-sm rounded-lg border px-3 py-2 ${inp}`}>
          {["PRCA", "CCA", "CPRA"].map((a) => <option key={a}>{a}</option>)}
        </select>
        <select value={event} onChange={(e) => setEvent(e.target.value)} disabled={noRules} data-testid="add-event" className={`text-sm rounded-lg border px-3 py-2 ${inp} disabled:opacity-50`}>
          {noRules ? <option value="">No events configured</option> : events.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <input value={rodeoName} onChange={(e) => setRodeoName(e.target.value)} placeholder="Rodeo name" data-testid="add-rodeo-name" className={`flex-1 min-w-[160px] text-sm rounded-lg border px-3 py-2 ${inp}`} />
        <input type="number" value={addedMoney} onChange={(e) => setAddedMoney(e.target.value)} placeholder="Added $" data-testid="add-money" className={`w-28 text-sm rounded-lg border px-3 py-2 ${inp}`} />
        <button onClick={save} disabled={saving || noRules} data-testid="add-save" className="inline-flex items-center gap-1.5 text-sm font-bold bg-[#C8000F] text-white rounded-lg px-4 py-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
        </button>
      </div>
      {noRules && <p className="text-xs text-[#B8860B] mt-2">No count rules are configured for {assoc} {CURRENT_SEASON[assoc] || 2026} yet. Set them in Settings → Rodeo Count Rules first.</p>}
    </div>
  );
}
